import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { bousanmSolideApi } from '../services/bousanmSolideApi';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { Download, Filter, RefreshCw, DollarSign, Activity, Users, Calendar, Search, X, FileSpreadsheet } from 'lucide-react';

const RapportsBS = () => {
  const { t } = useTranslation(['solide', 'common']);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [agents, setAgents] = useState([]);
  const [isLive, setIsLive] = useState(true); // Mode Live activé par défaut
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [chartCurrency, setChartCurrency] = useState('HTG'); // Devise pour le graphique
  
  // Fonction utilitaire pour obtenir la date locale au format YYYY-MM-DD
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filtres
  const [filters, setFilters] = useState({
    startDate: getLocalDateString(), // Par défaut aujourd'hui (Local)
    endDate: getLocalDateString(),
    agent: 'all'
  });

  useEffect(() => {
    fetchAgents();
    fetchReport();
  }, []);

  // Effet pour le Monitoring Temps Réel (Polling)
  useEffect(() => {
    let interval;
    if (isLive) {
      interval = setInterval(() => {
        fetchReport(true); // true = silencieux (pas de spinner global)
      }, 10000); // Rafraîchir toutes les 10 secondes pour plus de réactivité
    }
    return () => clearInterval(interval);
  }, [isLive, filters]);

  // Recharger quand les filtres changent
  useEffect(() => {
    fetchReport();
  }, [filters.agent, filters.startDate, filters.endDate]);

  const fetchAgents = async () => {
    try {
      const res = await bousanmSolideApi.getAllAgentsPortfolio();
      setAgents(res.agents || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReport = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const res = await bousanmSolideApi.getRapportsSolide({
        start_date: filters.startDate,
        end_date: filters.endDate,
        agent: filters.agent
      });
      setData(res);
      setLastUpdate(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const setPeriod = (type) => {
    const today = new Date();
    const todayStr = getLocalDateString(today);
    
    if (type === 'today') {
      setFilters({ ...filters, startDate: todayStr, endDate: todayStr });
    } else if (type === 'month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setFilters({ ...filters, startDate: getLocalDateString(firstDay), endDate: todayStr });
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      agent: 'all'
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await bousanmSolideApi.downloadRapportExcel({
        start_date: filters.startDate,
        end_date: filters.endDate,
        agent: filters.agent
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rapport_Bousanm_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Erreur lors de l'export Excel", error);
    } finally {
      setExporting(false);
    }
  };

  // Préparer les données pour Recharts selon la devise sélectionnée
  const chartData = data?.graph?.[chartCurrency.toLowerCase()]?.map(d => ({
    date: new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    volume: d.volume,
    transactions: d.transactions
  })) || [];

  return (
    <div className="h-full w-full bg-gray-50/50 overflow-y-auto p-4">
      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-gray-900">{t('solide:reportsPage.title')}</h1>
          </div>
          <p className="text-xs text-gray-500">
            {isLive ? t('solide:reportsPage.realTimeEnabled') : t('solide:reportsPage.manualMode')} • {t('solide:reportsPage.lastUpdate')}: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
          {/* Contrôles Live & Période */}
          <div className="flex items-center bg-white border border-gray-200 p-1 rounded-lg shadow-sm w-full sm:w-auto gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsLive(!isLive)}
              className={`h-7 px-2 text-[10px] font-medium uppercase flex items-center gap-2 ${isLive ? 'text-green-600 bg-green-50' : 'text-gray-500'}`}
            >
              {isLive && (
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
              {isLive ? t('solide:reportsPage.liveOn') : t('solide:reportsPage.liveOff')}
            </Button>
            <div className="h-4 w-px bg-gray-200 mx-1"></div>
            <Button variant="ghost" size="sm" onClick={() => setPeriod('today')} className="h-7 px-2 text-xs text-gray-600">{t('solide:reportsPage.today')}</Button>
            <Button variant="ghost" size="sm" onClick={() => setPeriod('month')} className="h-7 px-2 text-xs text-gray-600">{t('solide:reportsPage.month')}</Button>
          </div>

          {/* Filtres Compacts */}
          <div className="flex items-center bg-white border border-gray-200 p-1 rounded-lg shadow-sm w-full sm:w-auto">
            <div className="flex items-center px-2 border-r border-gray-100 gap-1">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              <input 
                type="date" 
                value={filters.startDate} 
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="h-7 text-xs border-none focus:ring-0 p-0 w-24 text-gray-600"
              />
              <span className="text-xs text-gray-400">-</span>
              <input 
                type="date" 
                value={filters.endDate} 
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="h-7 text-xs border-none focus:ring-0 p-0 w-24 text-gray-600"
              />
            </div>
            
            <div className="flex items-center px-2 gap-1">
              <Search className="h-3.5 w-3.5 text-gray-400" />
              <select 
                className="h-7 text-xs border-none focus:ring-0 p-0 bg-transparent w-32 text-gray-600"
                value={filters.agent}
                onChange={(e) => setFilters({...filters, agent: e.target.value})}
              >
                <option value="all">{t('solide:reportsPage.allAgents')}</option>
                {agents.map((a, i) => (
                  <option key={i} value={a.agent.username}>
                    {a.agent.nom_complet}
                  </option>
                ))}
              </select>
            </div>

            <Button onClick={clearFilters} size="sm" variant="ghost" className="h-7 w-7 p-0 ml-1 text-red-500 hover:bg-red-50" title={t('solide:reportsPage.clearFilters')}>
              <X className="h-3.5 w-3.5" />
            </Button>
            <div className="h-4 w-px bg-gray-200 mx-1"></div>

            <Button onClick={() => fetchReport(false)} size="sm" variant="ghost" className="h-7 w-7 p-0 ml-1 text-blue-600 hover:bg-blue-50">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <Button 
            onClick={handleExport} 
            disabled={exporting || !data || (data.kpi.volume_htg === 0 && data.kpi.volume_usd === 0)}
            title={(!data || (data.kpi.volume_htg === 0 && data.kpi.volume_usd === 0)) ? t('solide:reportsPage.noDataToExport') : t('solide:reportsPage.exportExcel')}
            variant="outline" 
            className="h-9 px-3 gap-2 bg-white hover:bg-gray-50 border-gray-200 text-green-700 hover:text-green-800 text-xs w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-3.5 w-3.5" />
            )}
            Excel
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex h-64 items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : data ? (
        <div className="space-y-4">
          {/* KPIs Cards Compactes */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="bg-white border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <CardContent className="p-3 flex items-center gap-3 relative z-10">
                <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0 text-blue-600 shadow-sm border border-blue-100">
                  <DollarSign className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{t('solide:reportsPage.kpis.volumeHtg')}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-bold text-gray-900 tracking-tight">{data.kpi.volume_htg.toLocaleString()}</span>
                    <span className="text-[10px] font-medium text-gray-400">G</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <CardContent className="p-3 flex items-center gap-3 relative z-10">
                <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 text-emerald-600 shadow-sm border border-emerald-100">
                  <DollarSign className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{t('solide:reportsPage.kpis.volumeUsd')}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-bold text-gray-900 tracking-tight">{data.kpi.volume_usd.toLocaleString()}</span>
                    <span className="text-[10px] font-medium text-emerald-500">$</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <CardContent className="p-3 flex items-center gap-3 relative z-10">
                <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center shrink-0 text-green-600 shadow-sm border border-green-100">
                  <Activity className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{t('solide:reportsPage.kpis.transactions')}</p>
                  <div className="text-lg font-bold text-gray-900 tracking-tight">{data.kpi.nombre_transactions}</div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <CardContent className="p-3 flex items-center gap-3 relative z-10">
                <div className="h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0 text-purple-600 shadow-sm border border-purple-100">
                  <Users className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">{t('solide:reportsPage.kpis.clients')}</p>
                  <div className="text-lg font-bold text-gray-900 tracking-tight">{data.kpi.clients_concernes}</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart Section Compact avec Recharts */}
          <Card className="border-none shadow-sm bg-white">
            <CardHeader className="pb-2 pt-4 px-4 border-b border-gray-50 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-gray-700">{t('solide:reportsPage.chart.title')}</CardTitle>
              <div className="flex gap-1 bg-gray-100 p-0.5 rounded-md">
                <button 
                  onClick={() => setChartCurrency('HTG')}
                  className={`text-[10px] px-2 py-0.5 rounded-md font-medium transition-all ${chartCurrency === 'HTG' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  HTG
                </button>
                <button 
                  onClick={() => setChartCurrency('USD')}
                  className={`text-[10px] px-2 py-0.5 rounded-md font-medium transition-all ${chartCurrency === 'USD' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  USD
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-[220px] w-full">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartCurrency === 'HTG' ? "#2563eb" : "#10b981"} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={chartCurrency === 'HTG' ? "#2563eb" : "#10b981"} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#6b7280' }} 
                        dy={10}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#6b7280' }} 
                        tickFormatter={(value) => `${value >= 1000 ? (value/1000).toFixed(1) + 'k' : value}`}
                      />
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        itemStyle={{ fontSize: '12px', color: '#1f2937', fontWeight: 500 }}
                        labelStyle={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}
                        formatter={(value) => [`${value.toLocaleString()} ${chartCurrency === 'HTG' ? 'G' : '$'}`, 'Volume']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="volume" 
                        stroke={chartCurrency === 'HTG' ? "#2563eb" : "#10b981"} 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorVolume)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-xs">
                    {t('solide:reportsPage.chart.noData', { currency: chartCurrency })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Monitoring Table Compact */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/30">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">{t('solide:reportsPage.table.title')}</h3>
              </div>
              <span className="text-[10px] text-gray-500">{t('solide:reportsPage.table.subtitle')}</span>
            </div>
            
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-50/50 text-gray-500 border-b font-medium">
                    <tr>
                      <th className="px-4 py-2">{t('solide:reportsPage.table.dateTime')}</th>
                      <th className="px-4 py-2">{t('solide:reportsPage.table.client')}</th>
                      <th className="px-4 py-2">{t('solide:reportsPage.table.account')}</th>
                      <th className="px-4 py-2 text-right">{t('solide:reportsPage.table.amount')}</th>
                      <th className="px-4 py-2">{t('solide:reportsPage.table.assignedAgent')}</th>
                      <th className="px-4 py-2">{t('solide:reportsPage.table.operator')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.recent_transactions.map((t) => (
                      <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                          {new Date(t.date).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 font-medium text-gray-900">
                          {t.client_nom}
                        </td>
                        <td className="px-4 py-2 font-mono text-[10px] text-gray-500">
                          {t.compte_code}
                        </td>
                        <td className="px-4 py-2 text-right font-medium">
                          <span className={t.devise === 'USD' ? 'text-emerald-600' : 'text-blue-600'}>
                            {t.montant.toLocaleString()} {t.devise === 'USD' ? '$' : 'G'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          @{t.agent}
                        </td>
                        <td className="px-4 py-2 text-gray-400 text-[10px]">
                          {t.fait_par}
                        </td>
                      </tr>
                    ))}
                    {data.recent_transactions.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-xs text-gray-400">
                          {t('solide:reportsPage.table.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RapportsBS;
